import {
  apiFetch,
  clearAuth,
  getRefreshToken,
  getToken,
  setRefreshToken,
  setToken,
  type DashboardAudience,
  ApiError,
} from "./api/client";
import { getApiBaseUrl } from "./config";

export {
  apiFetch,
  ApiError,
  clearAuth,
  getRefreshToken,
  getToken,
  setRefreshToken,
  setToken,
  type DashboardAudience,
} from "./api/client";

export type UserSession = {
  accessToken: string;
  refreshToken?: string;
  user: {
    id: string;
    name: string;
    email?: string | null;
    phone?: string | null;
    language?: "EN" | "SW";
    messageLanguage?: string;
  };
  dashboard: string;
};

export type UserPreferences = {
  id: string;
  language?: "EN" | "SW";
  messageLanguage: string;
  channelsSms?: boolean;
  channelsEmail?: boolean;
  channelsWhatsapp?: boolean;
  channelsPush?: boolean;
  whatsappOptInAt?: string | null;
  quietHoursStart?: string | null;
  quietHoursEnd?: string | null;
  minSeverity?: string;
};

export const authApi = {
  adminLogin: (email: string, password: string) =>
    apiFetch<{ requiresOtp: boolean; challengeToken: string; message: string; devOtp?: string }>(
      "/auth/admin/login",
      { method: "POST", body: JSON.stringify({ email, password }) },
    ),

  adminVerifyOtp: (email: string, otp: string, challengeToken: string) =>
    apiFetch<{
      accessToken: string;
      refreshToken: string;
      admin: { id: string; email: string; name: string; role: string };
      dashboard: string;
    }>("/auth/admin/verify-otp", {
      method: "POST",
      body: JSON.stringify({ email, otp, challengeToken }),
    }),

  adminMe: () =>
    apiFetch<{
      admin: {
        id: string;
        email: string;
        name: string;
        role: string;
        lastLoginAt?: string | null;
      };
      dashboard: string;
    }>("/auth/admin/me", { audience: "admin" }),

  logoutAdmin: async () => {
    try {
      await apiFetch<{ ok: boolean }>("/auth/admin/logout", {
        method: "POST",
        audience: "admin",
        body: JSON.stringify({ refreshToken: getRefreshToken("admin") }),
      });
    } catch {
      /* clear local session even if API is offline */
    } finally {
      clearAuth("admin");
    }
  },

  userLogin: (payload: { email?: string; phone?: string; password: string }) =>
    apiFetch<UserSession>("/auth/user/login", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  register: (payload: {
    name: string;
    email?: string;
    phone: string;
    password?: string;
    county?: string;
    ward?: string;
    language?: "EN" | "SW";
    country?: string;
    captchaToken?: string;
    channel?: string;
    inviteCode?: string;
  }) =>
    apiFetch<UserSession>("/auth/user/register", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  requestOtp: (payload: {
    phone: string;
    country?: string;
    captchaToken?: string;
    channel?: string;
  }) =>
    apiFetch<{ ok: boolean; message: string; devOtp?: string }>("/auth/user/otp/request", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  verifyOtp: (payload: {
    phone: string;
    otp: string;
    name?: string;
    country?: string;
    captchaToken?: string;
    channel?: string;
    inviteCode?: string;
    county?: string;
  }) =>
    apiFetch<UserSession>("/auth/user/otp/verify", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  forgotPassword: (email: string) =>
    apiFetch<{ ok: boolean; message: string; devResetUrl?: string }>("/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),

  resetPassword: (token: string, password: string) =>
    apiFetch<{ ok: boolean; message: string }>("/auth/reset-password", {
      method: "POST",
      body: JSON.stringify({ token, password }),
    }),

  logoutUser: async () => {
    try {
      await apiFetch<{ ok: boolean }>("/auth/user/logout", {
        method: "POST",
        audience: "user",
        body: JSON.stringify({ refreshToken: getRefreshToken("user") }),
      });
    } catch {
      /* clear local session even if API is offline */
    } finally {
      clearAuth("user");
    }
  },

  me: () =>
    apiFetch<{
      user: UserSession["user"] & {
        onboardingStep?: string | null;
        onboardingCompletedAt?: string | null;
        authProvider?: string | null;
        phoneVerified?: boolean;
      };
      dashboard?: string;
      appRoute?: string;
      onboardingComplete?: boolean;
    }>("/auth/user/me", {
      audience: "user",
    }),

  /** Full URL — browser navigates here for Google OAuth redirect. */
  googleStartUrl: (audience: DashboardAudience = "user") =>
    `${getApiBaseUrl()}/auth/google/start?audience=${audience}`,

  health: () =>
    apiFetch<{
      status: string;
      integrations: Record<string, string>;
      sendMode?: string;
      shadowStats?: {
        periodStartedAt?: string | null;
        startedAt?: string | null;
        shadowAlertCount?: number;
        total?: number;
        unlabelledCount?: number;
        unlabelled?: number;
      };
    }>("/health"),
};

export function persistSession(
  audience: DashboardAudience,
  tokens: { accessToken: string; refreshToken?: string | null | undefined },
) {
  setToken(audience, tokens.accessToken);
  if (tokens.refreshToken) setRefreshToken(audience, tokens.refreshToken);
}

export type UserPlace = {
  id: string;
  label: string;
  county: string;
  ward: string;
  latitude?: number | null;
  longitude?: number | null;
  h3Cell?: string | null;
  accuracy?: number | null;
  source?: string | null;
  capturedAt?: string | null;
  createdAt?: string;
};

export type LiveLocationRow = {
  ward?: string | null;
  county?: string | null;
  accuracy?: number | null;
  source?: string | null;
  capturedAt?: string | null;
  expiresAt?: string | null;
  h3Cell?: string | null;
  outsideCoverage?: boolean;
  stale?: boolean;
  latitude?: number | null;
  longitude?: number | null;
};

export type LocationStatus = {
  hasPlace: boolean;
  places: UserPlace[];
  liveConsent: boolean;
  lastLiveAt: string | null;
  lastLive: LiveLocationRow | null;
  live: LiveLocationRow | null;
  consentFlags?: Record<string, unknown> | null;
};

export type LiveLocationPayload = {
  latitude: number;
  longitude: number;
  accuracyM?: number;
  county?: string;
  ward?: string;
  source?: "gps" | "manual";
  live_location_while_open?: boolean;
  outsideCoverage?: boolean;
};

export type PostPlacePayload = {
  label: string;
  county: string;
  ward: string;
  latitude?: number;
  longitude?: number;
  accuracy?: number;
  source?: "gps" | "manual";
};

/** Places + live location — Stage B `/me/*` routes (also `/user/places`). */
export const locationApi = {
  getPlaces: () => apiFetch<{ places: UserPlace[] }>("/user/places", { audience: "user" }),

  postPlace: (payload: PostPlacePayload) =>
    apiFetch<{ place: UserPlace }>("/me/places", {
      method: "POST",
      audience: "user",
      body: JSON.stringify({
        label: payload.label,
        county: payload.county,
        ward: payload.ward,
        latitude: payload.latitude,
        longitude: payload.longitude,
        accuracy: payload.accuracy,
        source: payload.source ?? (payload.latitude != null ? "manual" : "manual"),
      }),
    }),

  getLocationStatus: async (): Promise<LocationStatus> => {
    const data = await apiFetch<{ places: UserPlace[]; live: LiveLocationRow | null }>(
      "/me/location",
      { audience: "user" },
    );
    const places = data.places || [];
    const live = data.live || null;
    let liveConsent = false;
    try {
      if (typeof window !== "undefined") {
        liveConsent = window.localStorage.getItem("anga_live_location_while_open") === "1";
      }
    } catch {
      /* ignore */
    }
    return {
      hasPlace: places.length > 0,
      places,
      liveConsent,
      lastLiveAt: live?.capturedAt || null,
      lastLive: live,
      live,
    };
  },

  postLiveLocation: async (payload: LiveLocationPayload) => {
    if (payload.live_location_while_open != null && typeof window !== "undefined") {
      try {
        window.localStorage.setItem(
          "anga_live_location_while_open",
          payload.live_location_while_open ? "1" : "0",
        );
      } catch {
        /* ignore */
      }
    }
    const result = await apiFetch<{ live: LiveLocationRow }>("/me/location/live", {
      method: "POST",
      audience: "user",
      body: JSON.stringify({
        lat: payload.latitude,
        lon: payload.longitude,
        accuracy: payload.accuracyM,
        source: payload.source ?? "gps",
        county: payload.county,
        ward: payload.ward,
        outsideCoverage: payload.outsideCoverage,
      }),
    });
    return {
      ok: true as const,
      lastLiveAt: result.live?.capturedAt || null,
      live: result.live,
      place: null as UserPlace | null,
      reverse:
        result.live?.county || result.live?.ward
          ? { county: result.live.county || payload.county, ward: result.live.ward || payload.ward }
          : payload.county || payload.ward
            ? { county: payload.county, ward: payload.ward }
            : null,
    };
  },

  /** Persist consent flags (place_storage required; live optional). */
  patchConsentFlags: (consentFlags: Record<string, unknown>) => {
    if (typeof window !== "undefined" && "live_location_while_open" in consentFlags) {
      try {
        window.localStorage.setItem(
          "anga_live_location_while_open",
          consentFlags["live_location_while_open"] ? "1" : "0",
        );
      } catch {
        /* ignore */
      }
    }
    return apiFetch<{ user: Record<string, unknown> }>("/user/onboarding", {
      method: "PATCH",
      audience: "user",
      body: JSON.stringify({
        consentFlags,
        consentTypes: [
          "place_storage",
          ...(consentFlags["live_location_while_open"]
            ? (["live_location_while_open"] as const)
            : []),
        ],
      }),
    });
  },
};

/** Stage C — message language + channel prefs (`/me/prefs`). */
export const prefsApi = {
  get: () => apiFetch<{ preferences: UserPreferences }>("/me/prefs", { audience: "user" }),

  patch: (body: Partial<UserPreferences>) =>
    apiFetch<{ preferences: UserPreferences }>("/me/prefs", {
      method: "PATCH",
      audience: "user",
      body: JSON.stringify(body),
    }),
};

/** Phone gate — add + verify a phone number for Google-signup users (`/me/phone/*`). */
export const phoneGateApi = {
  requestOtp: (phone: string, country?: string) =>
    apiFetch<{ ok: boolean; cooldownSec: number; devOtp?: string }>(
      "/me/phone/request-otp",
      { method: "POST", audience: "user", body: JSON.stringify({ phone, country }) },
    ),

  verifyOtp: (phone: string, otp: string, country?: string) =>
    apiFetch<{ ok: true }>(
      "/me/phone/verify-otp",
      { method: "POST", audience: "user", body: JSON.stringify({ phone, otp, country }) },
    ),
};

/** Resident self-report (crowd hazard) — moderated + DeepSeek triage. */
export const reportsApi = {
  list: () =>
    apiFetch<{
      reports: Array<{
        id: string;
        status: string;
        category?: string | null;
        severity?: string | null;
        county?: string | null;
        ward?: string | null;
        placeLabel?: string | null;
        description: string;
        rejectReason?: string | null;
        alertId?: string | null;
        createdAt: string;
        hazardType?: string | null;
      }>;
    }>("/me/reports", { audience: "user" }),

  submit: (body: {
    description: string;
    latitude: number;
    longitude: number;
    county?: string | null;
    ward?: string | null;
    placeLabel?: string | null;
    imageDataUrl?: string | null;
    hazardType?: string | null;
  }) =>
    apiFetch<{
      ok: boolean;
      code?: string;
      message?: string;
      error?: string;
      warned?: boolean;
      reportId?: string;
      alertId?: string;
      analysis?: Record<string, unknown>;
      fanout?: Record<string, unknown> | null;
    }>("/me/reports", {
      method: "POST",
      audience: "user",
      body: JSON.stringify(body),
    }),
};

/** Stage D self-test SMS — not exposed in resident UI (real-user onboarding). */
export const testAlertApi = {
  getBudget: () =>
    apiFetch<{ usedToday: number; cap: number; remainingToday: number }>("/me/test-alert", {
      audience: "user",
    }),
};

/** Stage E — admin broadcast composer confirm thresholds (mirror backend defaults). */
export const BROADCAST_CONFIRM_N = 50;
export const BROADCAST_CONFIRM_M = 500;

export type BroadcastCategory = "service_announcement" | "safety_advisory";
export type BroadcastStatus = "draft" | "scheduled" | "sending" | "sent" | "cancelled";
export type BroadcastEncoding = "gsm7" | "ucs2" | "unknown";

export type BroadcastAudience = {
  counties?: string[];
  county?: string;
  wards?: string[];
  areas?: string[];
  zoneIds?: string[];
  /** Staff / pilot test cohort filter */
  staffTestGroup?: boolean;
  pilotCohortOnly?: boolean;
  languages?: string[];
  personas?: string[];
  h3Cell?: string | null;
  severity?: string;
  [key: string]: unknown;
};

export type BroadcastVariantInput = {
  language: string;
  body: string;
  encoding?: BroadcastEncoding;
  smsEnabled?: boolean;
  reviewed?: boolean;
  backTranslation?: string | null;
};

export type BroadcastVariant = BroadcastVariantInput & {
  id: string;
  broadcastId: string;
  createdAt?: string;
  updatedAt?: string;
};

export type BroadcastRow = {
  id: string;
  category: BroadcastCategory;
  status: BroadcastStatus;
  title?: string | null;
  audienceJson: BroadcastAudience;
  scheduleAt?: string | null;
  createdBy?: string | null;
  reviewedAt?: string | null;
  sentAt?: string | null;
  confirmTyped?: string | null;
  secondApproverId?: string | null;
  excludeReasonsJson?: Record<string, number> | null;
  estimateJson?: BroadcastEstimate | null;
  createdAt: string;
  updatedAt: string;
  variants: BroadcastVariant[];
};

export type BroadcastReceipt = {
  userId: string;
  masked: string;
  ok: boolean;
  error?: string | null;
  provider?: string | null;
  at: string;
};

export type BroadcastSendResults = {
  attempted: number;
  accepted: number;
  failed: number;
  skippedEncoding?: number;
  dryRun?: boolean;
  receipts?: BroadcastReceipt[];
  receiptsTruncated?: boolean;
  samples?: Array<{ userId: string; masked: string; ok: boolean; provider?: string; at?: string }>;
};

export type BroadcastPlaces = {
  counties: Array<{
    county: string;
    users: number;
    wards: Array<{ ward: string; users: number }>;
  }>;
};

export type BroadcastEstimate = {
  count: number;
  candidateCount?: number;
  gatedCount?: number;
  gatedMode?: string;
  gatedReason?: string | null;
  excludeReasonsSample?: Array<{ userId: string; county?: string; reasons: string[] }>;
  excludeReasonCounts?: Record<string, number>;
  sendResults?: BroadcastSendResults;
  costKes: number;
  segmentsPerMsg?: number;
  encoding?: string;
  eta?: { minutes?: number; smsSeconds?: number; summary?: string } | Record<string, unknown>;
  probeStatus?: "ok" | "fail" | "unknown";
  variants?: Array<{ language: string; encoding?: string; smsEnabled?: boolean }>;
  killSwitch?: { paused: boolean; reason?: string };
  neverOfficial?: boolean;
  category?: BroadcastCategory;
};

export type BroadcastHistory = {
  page: number;
  pageSize: number;
  total: number;
  funnel: Record<BroadcastStatus, number>;
  byCategory: Record<string, number>;
  estimatedRecipientsOnPage?: number;
  broadcasts: BroadcastRow[];
};

export type BroadcastTranslateResult = {
  ok: boolean;
  text?: string;
  targetLang?: string;
  provider?: string;
  error?: string;
  code?: string;
  latencyMs?: number;
};

/** Stage E — admin broadcasts (`/admin/broadcasts/*`). */
export const broadcastsApi = {
  places: () =>
    apiFetch<BroadcastPlaces>("/admin/broadcasts/places", { audience: "admin" }),

  list: (params?: { page?: number; pageSize?: number }) => {
    const q = new URLSearchParams();
    if (params?.page) q.set("page", String(params.page));
    if (params?.pageSize) q.set("pageSize", String(params.pageSize));
    const qs = q.toString();
    return apiFetch<BroadcastHistory>(`/admin/broadcasts${qs ? `?${qs}` : ""}`, {
      audience: "admin",
    });
  },

  history: (params?: { page?: number; pageSize?: number }) => {
    const q = new URLSearchParams();
    if (params?.page) q.set("page", String(params.page));
    if (params?.pageSize) q.set("pageSize", String(params.pageSize));
    const qs = q.toString();
    return apiFetch<BroadcastHistory>(`/admin/broadcasts/history${qs ? `?${qs}` : ""}`, {
      audience: "admin",
    });
  },

  get: (id: string) =>
    apiFetch<{ broadcast: BroadcastRow }>(`/admin/broadcasts/${id}`, { audience: "admin" }),

  create: (body: {
    category: BroadcastCategory;
    title?: string | null;
    audienceJson: BroadcastAudience;
    variants: BroadcastVariantInput[];
    scheduleAt?: string | null;
  }) =>
    apiFetch<{ broadcast: BroadcastRow }>("/admin/broadcasts", {
      method: "POST",
      audience: "admin",
      body: JSON.stringify(body),
    }),

  update: (
    id: string,
    body: Partial<{
      category: BroadcastCategory;
      title: string | null;
      audienceJson: BroadcastAudience;
      variants: BroadcastVariantInput[];
      scheduleAt: string | null;
    }>,
  ) =>
    apiFetch<{ broadcast: BroadcastRow }>(`/admin/broadcasts/${id}`, {
      method: "PATCH",
      audience: "admin",
      body: JSON.stringify(body),
    }),

  cancel: (id: string) =>
    apiFetch<{ broadcast: BroadcastRow }>(`/admin/broadcasts/${id}/cancel`, {
      method: "POST",
      audience: "admin",
      body: JSON.stringify({}),
    }),

  estimate: (
    id: string,
    overrides?: {
      audienceJson?: BroadcastAudience;
      category?: BroadcastCategory;
      variants?: BroadcastVariantInput[];
    },
  ) =>
    apiFetch<{ estimate: BroadcastEstimate }>(`/admin/broadcasts/${id}/estimate`, {
      method: "POST",
      audience: "admin",
      body: JSON.stringify(overrides || {}),
    }),

  preview: (id: string) =>
    apiFetch<{
      estimate: BroadcastEstimate;
      previews: Array<{
        language: string;
        encoding: string;
        smsEnabled?: boolean;
        body: string;
        meta?: Record<string, unknown>;
      }>;
    }>(`/admin/broadcasts/${id}/preview`, {
      method: "POST",
      audience: "admin",
      body: JSON.stringify({}),
    }),

  translate: (id: string, body: { text?: string; targetLang: string; variantId?: string }) =>
    apiFetch<BroadcastTranslateResult>(`/admin/broadcasts/${id}/translate`, {
      method: "POST",
      audience: "admin",
      body: JSON.stringify(body),
    }),

  secondConfirm: (id: string) =>
    apiFetch<{ broadcast: BroadcastRow }>(`/admin/broadcasts/${id}/second-confirm`, {
      method: "POST",
      audience: "admin",
      body: JSON.stringify({}),
    }),

  send: (id: string, body?: { confirmTyped?: string; dryRun?: boolean }) =>
    apiFetch<{
      broadcast: BroadcastRow;
      estimate: BroadcastEstimate;
      results?: unknown;
      mode: string;
    }>(`/admin/broadcasts/${id}/send`, {
      method: "POST",
      audience: "admin",
      body: JSON.stringify(body || {}),
    }),

  schedule: (id: string, body: { scheduleAt?: string; confirmTyped?: string }) =>
    apiFetch<{
      broadcast: BroadcastRow;
      estimate: BroadcastEstimate;
      mode: string;
    }>(`/admin/broadcasts/${id}/schedule`, {
      method: "POST",
      audience: "admin",
      body: JSON.stringify(body),
    }),
};

/** Shared Stage F analytics query filters. */
export type AnalyticsQuery = {
  from: string;
  to: string;
  compareFrom?: string;
  compareTo?: string;
  /** Default false — sample users/alerts excluded */
  includeSample?: boolean;
  /** Default false — isTest deliveries excluded */
  includeTest?: boolean;
};

export type AnalyticsKpi = {
  key: string;
  label: string;
  value: number;
  unit?: string | null;
  previous?: number | null;
  deltaPct?: number | null;
};

export type AnalyticsOverview = {
  generatedAt: string;
  range: { from: string; to: string };
  compareRange?: { from: string; to: string } | null;
  excluded: { isSample: boolean; isTest: boolean };
  kpis: AnalyticsKpi[];
};

export type AnalyticsEngagement = {
  generatedAt: string;
  series: Array<{
    date: string;
    activeUsers?: number;
    alertOpens?: number;
    acks?: number;
    deliveries?: number;
    [key: string]: string | number | undefined;
  }>;
};

export type AnalyticsDemographics = {
  generatedAt: string;
  byLanguage: Array<{ key: string; label?: string; count: number }>;
  byPersona: Array<{ key: string; label?: string; count: number }>;
  byChannel: Array<{ key: string; label?: string; count: number }>;
  byAgeBand?: Array<{ key: string; label?: string; count: number }>;
};

export type AnalyticsGeography = {
  generatedAt: string;
  counties: Array<{
    county: string;
    users: number;
    inWarningZone?: number;
    exposureScore?: number;
  }>;
  /** Optional anonymized density for Mapbox (no exact PII points). */
  density?: {
    type: "FeatureCollection";
    features: Array<{
      type: "Feature";
      properties?: { weight?: number; county?: string; [key: string]: unknown };
      geometry: { type: "Point"; coordinates: [number, number] };
    }>;
  } | null;
};

export type AnalyticsAlertsFunnel = {
  generatedAt: string;
  funnel: {
    created: number;
    approved: number;
    sent: number;
    delivered: number;
    ack: number;
  };
  medianTimeToApproveMinutes?: number | null;
  medianTimeToSendMinutes?: number | null;
  p90TimeToApproveMinutes?: number | null;
  p90TimeToSendMinutes?: number | null;
  autoSendCount?: number;
  manualApproveCount?: number;
};

function analyticsQs(params: AnalyticsQuery): string {
  const q = new URLSearchParams();
  q.set("from", params.from);
  q.set("to", params.to);
  if (params.compareFrom) q.set("compareFrom", params.compareFrom);
  if (params.compareTo) q.set("compareTo", params.compareTo);
  if (params.includeSample) q.set("includeSample", "true");
  if (params.includeTest) q.set("includeTest", "true");
  return q.toString();
}

/** Stage F — admin analytics (`/admin/analytics/*`). Sample/test excluded unless toggled. */
export const analyticsApi = {
  overview: (params: AnalyticsQuery) =>
    apiFetch<AnalyticsOverview>(`/admin/analytics/overview?${analyticsQs(params)}`, {
      audience: "admin",
    }),

  engagement: (params: AnalyticsQuery) =>
    apiFetch<AnalyticsEngagement>(`/admin/analytics/engagement?${analyticsQs(params)}`, {
      audience: "admin",
    }),

  demographics: (params: AnalyticsQuery) =>
    apiFetch<AnalyticsDemographics>(`/admin/analytics/demographics?${analyticsQs(params)}`, {
      audience: "admin",
    }),

  geography: (params: AnalyticsQuery) =>
    apiFetch<AnalyticsGeography>(`/admin/analytics/geography?${analyticsQs(params)}`, {
      audience: "admin",
    }),

  alertsFunnel: (params: AnalyticsQuery) =>
    apiFetch<AnalyticsAlertsFunnel>(`/admin/analytics/alerts-funnel?${analyticsQs(params)}`, {
      audience: "admin",
    }),
};

export type FloodZoneRiskClass = "LOW" | "WATCH" | "WARNING" | "SEVERE";

export type FloodZoneRow = {
  id: string;
  name: string;
  county: string;
  riskClass: FloodZoneRiskClass | string;
  source?: string;
  notes?: string | null;
  polygon: unknown;
  version?: number;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  _count?: { h3Cells?: number };
};

export type UpdateFloodZonePayload = Partial<CreateFloodZonePayload> & {
  isActive?: boolean;
};

export type CreateFloodZonePayload = {
  name: string;
  county: string;
  riskClass: FloodZoneRiskClass;
  polygon: unknown;
  notes?: string | null;
  source?: string;
};

/** Stage F — flood zones (`/admin/zones`). */
export const zonesApi = {
  list: () => apiFetch<{ zones: FloodZoneRow[] }>("/admin/zones", { audience: "admin" }),

  create: (body: CreateFloodZonePayload) =>
    apiFetch<{ zone: FloodZoneRow }>("/admin/zones", {
      method: "POST",
      audience: "admin",
      body: JSON.stringify(body),
    }),

  get: (id: string) =>
    apiFetch<{ zone: FloodZoneRow }>(`/admin/zones/${id}`, { audience: "admin" }),

  update: (id: string, body: UpdateFloodZonePayload) =>
    apiFetch<{ zone: FloodZoneRow }>(`/admin/zones/${id}`, {
      method: "PATCH",
      audience: "admin",
      body: JSON.stringify(body),
    }),

  remove: (id: string) =>
    apiFetch<{ zone: FloodZoneRow }>(`/admin/zones/${id}`, {
      method: "DELETE",
      audience: "admin",
    }),
};

export type WeatherCurrent = {
  temperature: number;
  feelsLike: number;
  conditionText: string;
  conditionCode: number;
  conditionIcon: string;
  humidity: number;
  windKph: number;
  rainChancePct: number;
  lastUpdated: string;
  lat: number;
  lng: number;
};

export type HourlySlot = {
  hour: string;
  rainMm: number;
  rainChancePct: number;
  conditionCode: number;
};

export const weatherApi = {
  current: (params?: { lat?: number; lng?: number }) => {
    const qs = new URLSearchParams();
    if (params?.lat != null) qs.set("lat", String(params.lat));
    if (params?.lng != null) qs.set("lng", String(params.lng));
    const query = qs.toString();
    return apiFetch<WeatherCurrent>(`/user/weather/current${query ? `?${query}` : ""}`, { audience: "user" });
  },
  hourly: (params?: { lat?: number; lng?: number }) => {
    const qs = new URLSearchParams();
    if (params?.lat != null) qs.set("lat", String(params.lat));
    if (params?.lng != null) qs.set("lng", String(params.lng));
    const query = qs.toString();
    return apiFetch<{ hours: HourlySlot[] }>(`/user/forecast/hourly${query ? `?${query}` : ""}`, { audience: "user" });
  },
};

export type RouteRiskySegment = {
  startIndex: number;
  endIndex: number;
  severity: string;
  hazardType: string;
  label: string;
  coordinates: [number, number][];
};

export type ProcessedRoute = {
  index: number;
  geometry: { type: "LineString"; coordinates: [number, number][] };
  distance: number;  // metres
  duration: number;  // seconds
  riskySegments: RouteRiskySegment[];
  riskyCount: number;
};

export type RouteRisksResponse = {
  routes: ProcessedRoute[];
  safestRouteIndex: number;
  allRoutesDangerous: boolean;
  hasAlternative: boolean;
};

export const routeApi = {
  risks: (body: { from: [number, number]; to: [number, number]; profile?: string }) =>
    apiFetch<RouteRisksResponse>("/user/route/risks", {
      method: "POST",
      audience: "user",
      body: JSON.stringify(body),
    }),
};

export type ActiveSession = {
  id: string;
  ip?: string | null;
  userAgent?: string | null;
  createdAt: string;
  expiresAt: string;
};

export const settingsApi = {
  /** GET /auth/user/me — full user profile for settings */
  getProfile: () =>
    apiFetch<{
      user: {
        id: string;
        name?: string | null;
        email?: string | null;
        phone?: string | null;
        phoneVerified?: boolean;
        emailVerified?: boolean;
        authProvider?: string | null;
        county?: string | null;
        ward?: string | null;
        language?: string | null;
        messageLanguage?: string | null;
        personas?: unknown;
        onboardingCompletedAt?: string | null;
      };
      onboardingComplete?: boolean;
    }>("/auth/user/me", { audience: "user" }),

  /** PATCH /user/onboarding — update name / language */
  updateProfile: (body: { name?: string; language?: "EN" | "SW" }) =>
    apiFetch<{ ok: boolean }>("/user/onboarding", {
      method: "PATCH",
      audience: "user",
      body: JSON.stringify(body),
    }),

  /** GET /me/prefs — alert channel preferences */
  getPrefs: () =>
    apiFetch<{ preferences: UserPreferences }>("/me/prefs", { audience: "user" }),

  /** PATCH /me/prefs — update channels and alert language */
  patchPrefs: (body: Partial<UserPreferences>) =>
    apiFetch<{ preferences: UserPreferences }>("/me/prefs", {
      method: "PATCH",
      audience: "user",
      body: JSON.stringify(body),
    }),

  /** GET /me/location — saved places list */
  getPlaces: () =>
    apiFetch<{
      places: Array<{ id: string; label: string; county: string; ward: string; source?: string | null }>;
    }>("/me/location", { audience: "user" }),

  /** DELETE /me/places/:id */
  deletePlace: (id: string) =>
    apiFetch<{ ok: boolean }>(`/me/places/${id}`, { method: "DELETE", audience: "user" }),

  /** GET /auth/user/sessions */
  getSessions: () =>
    apiFetch<{ sessions: ActiveSession[] }>("/auth/user/sessions", { audience: "user" }),

  /** DELETE /auth/user/sessions — revoke all */
  revokeAllSessions: () =>
    apiFetch<{ ok: boolean }>("/auth/user/sessions", { method: "DELETE", audience: "user" }),

  /** POST /me/privacy-request — delete account or export data */
  privacyRequest: (type: "DELETION" | "EXPORT") =>
    apiFetch<{ ok: boolean }>("/me/privacy-request", {
      method: "POST",
      audience: "user",
      body: JSON.stringify({ type }),
    }),
};
